import { Col, Row } from "antd";
import cuid from "cuid";
import { FieldArray, Formik } from "formik";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import { MdOutlineEdit } from "react-icons/md";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { PROCESS, PROCESSES } from "utils/api/graphql/queries/processes";
import { WORKFLOWS } from "utils/api/graphql/queries/workflows";
import {
  ACTION_TYPES,
  BOOLEAN,
  DOCUMENTS_TYPES,
  PROCESS_TYPES,
} from "utils/constants";
import { bindArrayInputProps } from "utils/helpers/input";
import ConditionsModal from "../ConditionsModal";
import { sortProcessStatuses } from "../../sortProcessStatuses";

const actionComponents = ({
  FORM,
  index,
  i,
  values,
  formProps,
  setFieldValue,
}) => {
  const GENERATE_DOCUMENT_OPTIONS = [
    {
      value: DOCUMENTS_TYPES.QUOTE,
      label: FORM.ACTIONS.DOCUMENT_TYPES_OPTIONS.QUOTE,
    },
    {
      value: DOCUMENTS_TYPES.DUTY_OF_ADVICE_QUOTE,
      label: FORM.ACTIONS.DOCUMENT_TYPES_OPTIONS.DUTY_OF_ADVICE_QUOTE,
    },
  ];

  const DOWNLOAD_DOCUMENTS_OPTIONS = [
    {
      value: DOCUMENTS_TYPES.QUOTE,
      label: FORM.ACTIONS.DOCUMENT_TYPES_OPTIONS.QUOTE,
    },
    {
      value: DOCUMENTS_TYPES.DUTY_OF_ADVICE_QUOTE,
      label: FORM.ACTIONS.DOCUMENT_TYPES_OPTIONS.DUTY_OF_ADVICE_QUOTE,
    },
    {
      value: DOCUMENTS_TYPES.STATIC_INSURANCE_PLAN,
      label: FORM.ACTIONS.DOCUMENT_TYPES_OPTIONS.STATIC_INSURANCE_PLAN,
    },
  ];
  return {
    [ACTION_TYPES.NAVIGATE]: () => (
      <Col lg={10}>
        <Input
          label={FORM.ACTIONS.URL}
          placeholder={FORM.ACTIONS.URL}
          {...bindArrayInputProps({
            parent: `processStatuses.${index}.actions`,
            index: i,
            name: "args.url",
            values,
            ...formProps,
          })}
        />
      </Col>
    ),
    [ACTION_TYPES.GENERATE_DOCUMENT]: () => (
      <Col lg={10}>
        <Select
          showSearch
          options={GENERATE_DOCUMENT_OPTIONS}
          label={FORM.ACTIONS.DOCUMENT_TYPE}
          placeholder={FORM.ACTIONS.DOCUMENT_TYPE}
          {...bindArrayInputProps({
            parent: `processStatuses.${index}.actions`,
            index: i,
            name: "args.documentType",
            values,
            ...formProps,
          })}
        />
      </Col>
    ),
    [ACTION_TYPES.DOWNLOAD_DOCUMENTS]: () => (
      <Col lg={10}>
        <Select
          showSearch
          mode="multiple"
          options={DOWNLOAD_DOCUMENTS_OPTIONS}
          label={FORM.ACTIONS.DOCUMENT_TYPES}
          placeholder={FORM.ACTIONS.DOCUMENT_TYPES}
          {...bindArrayInputProps({
            parent: `processStatuses.${index}.actions`,
            index: i,
            name: "args.documentTypes",
            values,
            ...formProps,
          })}
          value={
            get(
              values,
              `processStatuses.${index}.actions.${i}.args.documentTypes`,
            ) || []
          }
          onChange={(value) =>
            setFieldValue(
              `processStatuses.${index}.actions.${i}.args.documentTypes`,
              value,
            )
          }
        />
      </Col>
    ),
    [ACTION_TYPES.WEBHOOK]: () => (
      <Col lg={6}>
        <Select
          showSearch
          type="async"
          label={FORM.ACTIONS.WEBHOOKS}
          placeholder={FORM.ACTIONS.WEBHOOKS}
          node={{
            query: WORKFLOWS,
            variables: {},
            accessors: { root: "workflows.data" },
          }}
          {...bindArrayInputProps({
            parent: `processStatuses.${index}.actions`,
            index: i,
            name: "args.workflow",
            values,
            ...formProps,
          })}
        />
      </Col>
    ),
    [ACTION_TYPES.SYNC_PROCESSES]: () => (
      <>
        <Col lg={5}>
          <Select
            showSearch
            type="async"
            label={FORM.ACTIONS.PROCESS.LABEL}
            placeholder={FORM.ACTIONS.PROCESS.PLACEHOLDER}
            node={{
              query: PROCESSES,
              variables: {},
              accessors: { root: "processes.data" },
            }}
            {...bindArrayInputProps({
              parent: `processStatuses.${index}.actions`,
              index: i,
              name: "args.process",
              values,
              ...formProps,
            })}
          />
        </Col>
        <Col lg={5}>
          <Select
            showSearch
            type="async"
            label={FORM.ACTIONS.PROCESS.STATUS}
            placeholder={FORM.ACTIONS.PROCESS.STATUS}
            node={{
              query: PROCESS,
              variables: {
                where: {
                  id: get(
                    values,
                    `processStatuses.${index}.actions.${i}.args.process`,
                  ),
                },
              },
              accessors: {
                root: "process.processStatuses",
              },
            }}
            {...bindArrayInputProps({
              parent: `processStatuses.${index}.actions`,
              index: i,
              name: "args.status",
              values,
              ...formProps,
            })}
          />
        </Col>
      </>
    ),
    [ACTION_TYPES.NO_SYNC_PROCESSES]: () => (
      <>
        <Col lg={5}>
          <Select
            showSearch
            type="async"
            label={FORM.ACTIONS.PROCESS.LABEL}
            placeholder={FORM.ACTIONS.PROCESS.PLACEHOLDER}
            node={{
              query: PROCESSES,
              variables: {},
              accessors: { root: "processes.data" },
            }}
            {...bindArrayInputProps({
              parent: `processStatuses.${index}.actions`,
              index: i,
              name: "args.process",
              values,
              ...formProps,
            })}
          />
        </Col>
      </>
    ),
    [ACTION_TYPES.SEND_ESIGN_REQUEST]: () => {
      set(values, `processStatuses.${index}.actions.${i}.args`, {});
      return <></>;
    },
    [ACTION_TYPES.SEND_SUBSCRIPTION_DOCUMENTS]: () => {
      set(values, `processStatuses.${index}.actions.${i}.args`, {});
      return <></>;
    },
    [ACTION_TYPES.SEND_ADDITIONAL_DOCUMENTS]: () => {
      set(values, `processStatuses.${index}.actions.${i}.args`, {});
      return <></>;
    },
    [ACTION_TYPES.ASSIGN_PROJECT]: () => {
      set(values, `processStatuses.${index}.actions.${i}.args`, {});
      return <></>;
    },
    [ACTION_TYPES.NOTIFY_ADVISOR]: () => {
      set(values, `processStatuses.${index}.actions.${i}.args`, {});
      return <></>;
    },
    [ACTION_TYPES.TRACK_CONVERSION]: () => (
      <Col lg={10}>
        <Input
          label={FORM.ACTIONS.CONVERSION_LABEL}
          placeholder={FORM.ACTIONS.CONVERSION_LABEL}
          {...bindArrayInputProps({
            parent: `processStatuses.${index}.actions`,
            index: i,
            name: "args.label",
            values,
            ...formProps,
          })}
        />
      </Col>
    ),
    [ACTION_TYPES.TRACK_SERVER_SIDE_EVENT]: () => (
      <Col lg={10}>
        <Input
          label={FORM.ACTIONS.GTM_EVENT}
          placeholder={FORM.ACTIONS.GTM_EVENT_PLACEHOLDER}
          {...bindArrayInputProps({
            parent: `processStatuses.${index}.actions`,
            index: i,
            name: "args.gtmEvent",
            values,
            ...formProps,
          })}
        />
      </Col>
    ),
    undefined: () => <></>,
  };
};

const PossibleStatuses = ({
  formRef,
  dirtyRef,
  setIsDirty,
  TABS,
  onSubmit,
  processStatuses,
  type,
  theme,
  modal,
  setModal,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });

  return (
    <Formik
      innerRef={(formik) => {
        formRef.current = formik;
      }}
      enableReinitialize={true}
      initialValues={{
        processStatuses: sortProcessStatuses(processStatuses || []),
      }}
      onSubmit={async (values) => {
        await onSubmit({
          type: type || PROCESS_TYPES.B2B,
          name: "",
          theme: theme || null,
          description: "",
          skills: [],
          config: [],
          attributionType: null,
          processStatuses: values.processStatuses,
          insurancePlans: [],
        });
        setIsDirty((prev) => ({
          ...prev,
          [TABS.POSSIBLE_STATUSES]: false,
        }));
      }}
    >
      {({
        values: possibleStatusesValues,
        setFieldValue: setPossibleStatusesFieldValue,
        dirty,
        ...possibleStatusesFormProps
      }) => {
        if (dirtyRef.current !== dirty) {
          dirtyRef.current = dirty;
          setIsDirty((prev) => ({
            ...prev,
            [TABS.POSSIBLE_STATUSES]: dirty,
          }));
        }

        return (
          <Card
            type="simple"
            head={false}
            title={FORM.POSSIBLE_STATUSES.POSSIBLE_STATUSES_INFO}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form>
                  {possibleStatusesValues.processStatuses.map(
                    (processStatus, index) => [
                      <Col span={4} key={`left_${processStatus.id}`}>
                        <h4> {processStatus.name || `Status ${index + 1}`} </h4>
                      </Col>,
                      <Col
                        span={20}
                        className="grid--1"
                        key={`right_${processStatus.id}`}
                      >
                        <Col span={24}>
                          <Row gutter={[16, 16]}>
                            <FieldArray
                              name={`processStatuses.${index}.possibleStatuses`}
                              render={(arrayHelpers) => [
                                possibleStatusesValues.processStatuses[
                                  index
                                ]?.possibleStatuses?.map((_, i) => [
                                  <Col lg={5} key={`nextStatus-${_.id}`}>
                                    <Select
                                      showSearch
                                      options={possibleStatusesValues.processStatuses
                                        .filter(
                                          (status) =>
                                            status.id !== processStatus.id,
                                        )
                                        .map((processStatus) => ({
                                          label:
                                            processStatus.name ||
                                            `Status ${index + 1}`,
                                          value: processStatus.id,
                                        }))}
                                      placeholder={
                                        FORM.POSSIBLE_STATUSES.NEXT_STATUS
                                      }
                                      label={FORM.POSSIBLE_STATUSES.NEXT_STATUS}
                                      {...bindArrayInputProps({
                                        parent: `processStatuses.${index}.possibleStatuses`,
                                        index: i,
                                        name: "nextStatus",
                                        values: possibleStatusesValues,
                                        ...possibleStatusesFormProps,
                                      })}
                                    />
                                  </Col>,
                                  <Col lg={4} key={`conditionLeft-${_.id}`}>
                                    <Input
                                      placeholder={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS
                                          .ACCESSOR
                                      }
                                      label={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS
                                          .ACCESSOR
                                      }
                                      {...bindArrayInputProps({
                                        parent: `processStatuses.${index}.possibleStatuses`,
                                        index: i,
                                        name: "conditions.0.leftOperand.accessor",
                                        values: possibleStatusesValues,
                                        ...possibleStatusesFormProps,
                                      })}
                                    />
                                  </Col>,
                                  <Col lg={4} key={`condition-${_.id}`}>
                                    <Input
                                      label={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS
                                          .OPERATION
                                      }
                                      placeholder={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS
                                          .OPERATION
                                      }
                                      {...bindArrayInputProps({
                                        parent: `processStatuses.${index}.possibleStatuses`,
                                        index: i,
                                        name: "conditions.0.operation",
                                        values: possibleStatusesValues,
                                        ...possibleStatusesFormProps,
                                      })}
                                    />
                                  </Col>,
                                  <Col lg={4} key={`conditionRight-${_.id}`}>
                                    <Input
                                      placeholder={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS.VALUE
                                      }
                                      label={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS.VALUE
                                      }
                                      {...bindArrayInputProps({
                                        parent: `processStatuses.${index}.possibleStatuses`,
                                        index: i,
                                        name: "conditions.0.rightOperand.value",
                                        values: possibleStatusesValues,
                                        ...possibleStatusesFormProps,
                                      })}
                                      onChange={(e) => {
                                        setPossibleStatusesFieldValue(
                                          `processStatuses.${index}.possibleStatuses.${i}.conditions.0.rightOperand.value`,
                                          BOOLEAN[e.target.value] ??
                                            e.target.value,
                                        );
                                      }}
                                    />
                                  </Col>,
                                  <Col span={2}>
                                    <Input
                                      type="number"
                                      placeholder={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS.ORDER
                                      }
                                      label={
                                        FORM.POSSIBLE_STATUSES.CONDITIONS.ORDER
                                      }
                                      {...bindArrayInputProps({
                                        parent: `processStatuses.${index}.possibleStatuses`,
                                        index: i,
                                        name: "order",
                                        values: possibleStatusesValues,
                                        ...possibleStatusesFormProps,
                                      })}
                                    />
                                  </Col>,
                                  <Col
                                    lg={1}
                                    key={`conditionRight_checkbox-${_.id}`}
                                  >
                                    <div
                                      onClick={() => {
                                        setModal({
                                          visible: true,
                                          name: "possibleStatuses",
                                          processStatusIndex: index,
                                          childIndex: i,
                                        });
                                      }}
                                    >
                                      <MdOutlineEdit size={16} />
                                    </div>
                                  </Col>,
                                  <Col lg={2} key={`delete-${_.id}`}>
                                    <div
                                      className={"delete-icon"}
                                      onClick={() => {
                                        arrayHelpers.remove(i);
                                      }}
                                    >
                                      <BiTrash
                                        color={COLORS.C_DANGER}
                                        size={24}
                                      />
                                    </div>
                                  </Col>,
                                ]),
                                <Button
                                  key="add-row"
                                  type="primary"
                                  onClick={() => {
                                    arrayHelpers.push({
                                      id: cuid(),
                                      conditions: [
                                        {
                                          id: cuid(),
                                          leftOperand: {},
                                          rightOperand: {},
                                        },
                                      ],
                                    });
                                  }}
                                >
                                  <BiPlus size={24} />
                                </Button>,
                              ]}
                            />
                          </Row>
                        </Col>
                        <Col span={24}>
                          <div className="grid--1">
                            <FieldArray
                              name={`processStatuses.${index}.actions`}
                              render={(arrayHelpers) => [
                                possibleStatusesValues.processStatuses[
                                  index
                                ]?.actions?.map((_, i) => (
                                  <Row gutter={[16, 16]} key={_.id}>
                                    <Col lg={6}>
                                      <Select
                                        showSearch
                                        options={Object.values(
                                          ACTION_TYPES,
                                        ).map((value) => ({
                                          value,
                                          label:
                                            FORM.ACTIONS.ACTION_TYPES[value],
                                        }))}
                                        placeholder={FORM.ACTIONS.TYPE}
                                        label={FORM.ACTIONS.TYPE}
                                        {...bindArrayInputProps({
                                          parent: `processStatuses.${index}.actions`,
                                          index: i,
                                          name: "type",
                                          values: possibleStatusesValues,
                                          ...possibleStatusesFormProps,
                                        })}
                                      />
                                    </Col>
                                    {actionComponents({
                                      FORM,
                                      index,
                                      i,
                                      values: possibleStatusesValues,
                                      formProps: possibleStatusesFormProps,
                                      setFieldValue:
                                        setPossibleStatusesFieldValue,
                                    })[
                                      get(
                                        possibleStatusesValues,
                                        `processStatuses.${index}.actions.${i}.type`,
                                      )
                                    ]()}
                                    <Col lg={1}>
                                      <div
                                        onClick={() => {
                                          setModal({
                                            visible: true,
                                            name: "actions",
                                            processStatusIndex: index,
                                            childIndex: i,
                                          });
                                        }}
                                      >
                                        <MdOutlineEdit size={16} />
                                      </div>
                                    </Col>
                                    <Col lg={2}>
                                      <div
                                        className={"delete-icon"}
                                        onClick={() => {
                                          arrayHelpers.remove(i);
                                        }}
                                      >
                                        <BiTrash
                                          color={COLORS.C_DANGER}
                                          size={24}
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                )),
                                <Button
                                  key="add-row"
                                  type="secondary"
                                  onClick={() => {
                                    arrayHelpers.push({
                                      id: cuid(),
                                    });
                                  }}
                                >
                                  <BiPlus size={24} /> {FORM.ACTIONS.ADD}
                                </Button>,
                              ]}
                            />
                          </div>
                        </Col>
                      </Col>,
                    ],
                  )}
                </Form>
              </Col>
            </Row>
            <ConditionsModal
              translation={FORM.POSSIBLE_STATUSES.CONDITIONS}
              modal={modal}
              setModal={setModal}
              setFieldValue={setPossibleStatusesFieldValue}
              values={possibleStatusesValues}
              formProps={possibleStatusesFormProps}
            />
          </Card>
        );
      }}
    </Formik>
  );
};

export default PossibleStatuses;
